/** B2B / professional portal strings. Merged into the main dictionaries as `b2b`. */
export const b2bEn = {
  entry: {
    eyebrow: "Welcome",
    title: "How will you use Inner Compass?",
    subtitle: "Choose the environment that fits you. You can always switch later.",
    personalTitle: "For myself",
    personalBody: "Your personal companion, check-ins and insights. Create a free account — no invitation needed.",
    personalCta: "Continue personally",
    businessTitle: "For my organisation",
    businessBody: "The professional environment for HR, managers, healthcare and occupational-health professionals.",
    businessCta: "Continue as professional",
    businessHint: "Received an invitation? Open the link in your email or sign in with your work account.",
    orgCta: "Onboard my organisation",
  },
  auth: {
    personalEyebrow: "Personal",
    businessEyebrow: "Organisation",
    businessTitle: "Sign in to your professional environment",
    businessSub: "For HR, managers, care and occupational-health professionals.",
    haveInviteQ: "I have an invitation code",
    inviteOptional: "Optional — only needed when your organisation invited you.",
    noInviteNeeded: "No invitation needed for a personal account.",
    switchAudience: "Not you? Choose a different entry",
    orgOnboard: "Want to onboard your organisation? Email",
  },
  nav: {
    owner: "Platform",
    organisations: "Organisations",
    billing: "Billing",
    roles: "Roles & access",
    employees: "Employees",
    insights: "Insights",
    licence: "Licence & billing",
    orgSettings: "Organisation settings",
    oh: "Occupational health",
    consent: "Consent centre",
    personal: "Personal",
    professional: "Professional",
    team: "My team",
  },
  owner: {
    title: "Platform overview",
    subtitle: "Organisations, licences and privileged access across Inner Compass.",
    orgs: "Organisations",
    activeOrgs: "Active organisations",
    users: "Users",
    activeLicences: "Active licences",
    seats: "Seats purchased",
    pendingInvites: "Open invitations",
    recentAudit: "Recent administrative activity",
    orgDetail: "Organisation detail",
    members: "Members",
    status: "Status",
    created: "Created",
    seatLimit: "Seat limit (invites)",
    updateLimit: "Update limit",
    openOrg: "Open",
    searchOrgs: "Search organisations…",
    billingTitle: "B2B billing",
    billingSub: "Stripe-backed organisation licences. Webhooks are the source of truth.",
    rolesTitle: "Roles & access",
    rolesSub: "Grant or revoke privileged professional roles. Every change is audited.",
    grantOh: "Grant occupational-health role",
    grantee: "Professional email",
    grant: "Grant",
    revoke: "Revoke",
    auditTitle: "Audit trail",
    auditSub: "Sensitive administrative actions. Never contains journal, chat or health content.",
    noStripe: "No Stripe billing yet",
    ohLinks: "Occupational-health relationships",
    source: "Source",
    platformAudit: "Platform audit (organisations, people, invites)",
    b2bAudit: "B2B audit (billing, roles, consent)",
  },
  billing: {
    title: "Licence & billing",
    subtitle: "Seat-based licence for your organisation. Pricing comes from Stripe — the UI never sets prices.",
    perSeat: "per person / month",
    perSeatYear: "per person / year",
    seats: "Seats",
    purchased: "Purchased",
    assigned: "Assigned",
    available: "Available",
    status: "Status",
    renews: "Current period ends",
    cancelsAt: "Ends at period end",
    choosePlan: "Choose a plan",
    seatCount: "Number of seats",
    startCheckout: "Continue to secure payment",
    total: "Estimated total",
    excl: "excl. VAT",
    manage: "Manage payment & invoices",
    changeSeats: "Change seats",
    saveSeats: "Update seats",
    cancel: "Cancel at period end",
    resume: "Keep subscription",
    enterprise: "Larger or custom arrangement?",
    enterpriseBody: "For enterprise agreements, pilots or manually granted licences, contact us. We do not invent prices.",
    contact: "Contact",
    checking: "Confirming your payment with Stripe…",
    confirmed: "Your licence is active. You can now invite employees within your seats.",
    cancelled: "Checkout cancelled. Nothing was charged.",
    paymentFailed: "Last payment failed — please update your payment method.",
    states: {
      none: "No licence", active: "Active", trialing: "Trial", past_due: "Payment overdue", unpaid: "Unpaid",
      canceled: "Cancelled", cancelled: "Cancelled", incomplete: "Pending", incomplete_expired: "Expired", paused: "Paused",
    },
    noOrg: "Your account is not linked to an organisation yet.",
    history: "Billing activity",
    monthly: "Monthly",
    yearly: "Yearly",
    seatsHint: "You can increase seats at any time. Reducing below assigned seats is not allowed.",
  },
  oh: {
    title: "Occupational health",
    subtitle: "Work only with information employees have explicitly chosen to share with you.",
    clients: "Employees",
    active: "Active consents",
    pending: "Awaiting consent",
    invite: "Request access",
    inviteBody: "The employee receives a request in their Consent centre and decides which categories to share. Nothing is visible until they accept.",
    purpose: "Purpose",
    message: "Message to employee (optional)",
    shared: "Shared with you",
    nothingShared: "Nothing is shared. The employee has not (or no longer) consented.",
    withdraw: "Close relationship",
    noClients: "No employees linked yet.",
    detail: "Employee overview",
    dataNote: "Shared trend data is delivered through the Inner Compass backend once the B2B service is connected to it.",
    statuses: { pending: "Pending", active: "Active", revoked: "Revoked by employee", declined: "Declined", closed: "Closed" },
  },
  consent: {
    title: "Consent centre",
    subtitle: "Who can see what, why, since when — and how to withdraw it at any time.",
    orgSharing: "Sharing with your organisation",
    orgSharingBody: "Only aggregate, anonymised signals. Never your messages, notes or check-in explanations.",
    care: "Care professionals (GGZ)",
    oh: "Occupational health (bedrijfsarts)",
    noRequests: "No requests or active consents.",
    requestFrom: "Request from",
    grantedOn: "Granted on",
    revokedOn: "Withdrawn on",
    categories: "What is shared",
    accept: "Share selected",
    decline: "Decline",
    revoke: "Withdraw consent",
    revokeConfirm: "Withdraw consent? Access is removed immediately.",
    review: "Review request",
    activeLabel: "Active",
    pendingLabel: "Awaiting your decision",
    whyTitle: "Why this matters",
    whyBody: "Your employer pays for a licence, not for access to your data. Only you decide what a professional may see — per relationship, per category.",
    cat: {
      wellbeing: "Wellbeing trend", mood: "Mood", stress_anxiety: "Stress & anxiety", sleep: "Sleep", checkins: "Check-ins (scores only)",
      weekly_summaries: "Weekly summaries", work_capacity: "Work capacity signals", reintegration_notes: "Reintegration notes",
      crisis_alerts: "Crisis alerts", safety_plan: "Safety plan", goals: "Goals", journal: "Journal", ai_conversations: "AI conversations",
    },
    mode: "Sharing mode",
  },
  flm: {
    title: "My team",
    subtitle: "Only participation and agreed aggregate trends — never individual content.",
    privacyNote: "Figures are hidden for groups smaller than the privacy threshold.",
  },
  common: {
    seatsUnit: "seats",
    seatUnit: "seat",
    none: "—",
    back: "Back",
    open: "Open",
    refresh: "Refresh",
    privacyBadge: "Privacy-safe aggregate",
  },
};

export const b2bNl = {
  entry: {
    eyebrow: "Welkom",
    title: "Hoe wil je Inner Compass gebruiken?",
    subtitle: "Kies de omgeving die bij je past. Je kunt later altijd wisselen.",
    personalTitle: "Voor mijzelf",
    personalBody: "Je persoonlijke companion, check-ins en inzichten. Maak gratis een account — geen uitnodiging nodig.",
    personalCta: "Persoonlijk verder",
    businessTitle: "Voor mijn organisatie",
    businessBody: "De professionele omgeving voor HR, leidinggevenden, zorg- en arboprofessionals.",
    businessCta: "Verder als professional",
    businessHint: "Uitnodiging ontvangen? Open de link in je e-mail of log in met je werkaccount.",
    orgCta: "Mijn organisatie aanmelden",
  },
  auth: {
    personalEyebrow: "Persoonlijk",
    businessEyebrow: "Organisatie",
    businessTitle: "Log in op je professionele omgeving",
    businessSub: "Voor HR, leidinggevenden, zorg- en arboprofessionals.",
    haveInviteQ: "Ik heb een uitnodigingscode",
    inviteOptional: "Optioneel — alleen nodig als je organisatie je heeft uitgenodigd.",
    noInviteNeeded: "Voor een persoonlijk account is geen uitnodiging nodig.",
    switchAudience: "Niet jij? Kies een andere ingang",
    orgOnboard: "Wil je jouw organisatie aanmelden? Mail",
  },
  nav: {
    owner: "Platform",
    organisations: "Organisaties",
    billing: "Facturatie",
    roles: "Rollen & toegang",
    employees: "Medewerkers",
    insights: "Inzichten",
    licence: "Licentie & facturatie",
    orgSettings: "Organisatie-instellingen",
    oh: "Bedrijfsarts",
    consent: "Toestemmingscentrum",
    personal: "Persoonlijk",
    professional: "Professioneel",
    team: "Mijn team",
  },
  owner: {
    title: "Platformoverzicht",
    subtitle: "Organisaties, licenties en bevoorrechte toegang binnen Inner Compass.",
    orgs: "Organisaties",
    activeOrgs: "Actieve organisaties",
    users: "Gebruikers",
    activeLicences: "Actieve licenties",
    seats: "Gekochte seats",
    pendingInvites: "Open uitnodigingen",
    recentAudit: "Recente beheeractiviteit",
    orgDetail: "Organisatiedetail",
    members: "Leden",
    status: "Status",
    created: "Aangemaakt",
    seatLimit: "Seatlimiet (uitnodigingen)",
    updateLimit: "Limiet bijwerken",
    openOrg: "Openen",
    searchOrgs: "Zoek organisaties…",
    billingTitle: "B2B-facturatie",
    billingSub: "Organisatielicenties via Stripe. Webhooks zijn de bron van waarheid.",
    rolesTitle: "Rollen & toegang",
    rolesSub: "Verleen of trek bevoorrechte professionele rollen in. Elke wijziging wordt gelogd.",
    grantOh: "Bedrijfsartsrol verlenen",
    grantee: "E-mail professional",
    grant: "Verlenen",
    revoke: "Intrekken",
    auditTitle: "Auditlog",
    auditSub: "Gevoelige beheeracties. Bevat nooit dagboek-, chat- of gezondheidsinhoud.",
    noStripe: "Nog geen Stripe-facturatie",
    ohLinks: "Bedrijfsartsrelaties",
    source: "Bron",
    platformAudit: "Platformaudit (organisaties, mensen, uitnodigingen)",
    b2bAudit: "B2B-audit (facturatie, rollen, toestemming)",
  },
  billing: {
    title: "Licentie & facturatie",
    subtitle: "Licentie per medewerker voor jouw organisatie. Prijzen komen uit Stripe — de interface bepaalt nooit prijzen.",
    perSeat: "per persoon / maand",
    perSeatYear: "per persoon / jaar",
    seats: "Seats",
    purchased: "Gekocht",
    assigned: "Toegewezen",
    available: "Beschikbaar",
    status: "Status",
    renews: "Huidige periode eindigt",
    cancelsAt: "Stopt aan einde periode",
    choosePlan: "Kies een plan",
    seatCount: "Aantal seats",
    startCheckout: "Door naar veilige betaling",
    total: "Geschat totaal",
    excl: "excl. btw",
    manage: "Betaalmethode & facturen beheren",
    changeSeats: "Seats wijzigen",
    saveSeats: "Seats bijwerken",
    cancel: "Opzeggen per einde periode",
    resume: "Abonnement behouden",
    enterprise: "Grotere of maatwerkafspraak?",
    enterpriseBody: "Voor enterprise-afspraken, pilots of handmatig verleende licenties: neem contact op. Wij verzinnen geen prijzen.",
    contact: "Contact",
    checking: "We bevestigen je betaling bij Stripe…",
    confirmed: "Je licentie is actief. Je kunt nu medewerkers uitnodigen binnen je seats.",
    cancelled: "Betaling geannuleerd. Er is niets afgeschreven.",
    paymentFailed: "Laatste betaling mislukt — werk je betaalmethode bij.",
    states: {
      none: "Geen licentie", active: "Actief", trialing: "Proef", past_due: "Betaling achterstallig", unpaid: "Onbetaald",
      canceled: "Opgezegd", cancelled: "Opgezegd", incomplete: "In afwachting", incomplete_expired: "Verlopen", paused: "Gepauzeerd",
    },
    noOrg: "Je account is nog niet aan een organisatie gekoppeld.",
    history: "Facturatie-activiteit",
    monthly: "Maandelijks",
    yearly: "Jaarlijks",
    seatsHint: "Je kunt seats altijd verhogen. Verlagen onder het aantal toegewezen seats is niet mogelijk.",
  },
  oh: {
    title: "Bedrijfsarts",
    subtitle: "Werk uitsluitend met informatie die medewerkers bewust met jou delen.",
    clients: "Medewerkers",
    active: "Actieve toestemmingen",
    pending: "Wacht op toestemming",
    invite: "Toegang vragen",
    inviteBody: "De medewerker ontvangt een verzoek in het Toestemmingscentrum en kiest zelf welke categorieën worden gedeeld. Tot die tijd is niets zichtbaar.",
    purpose: "Doel",
    message: "Bericht aan medewerker (optioneel)",
    shared: "Gedeeld met jou",
    nothingShared: "Er wordt niets gedeeld. De medewerker heeft geen (of niet langer) toestemming gegeven.",
    withdraw: "Relatie sluiten",
    noClients: "Nog geen medewerkers gekoppeld.",
    detail: "Medewerkeroverzicht",
    dataNote: "Gedeelde trenddata wordt geleverd via de Inner Compass-backend zodra de B2B-service daarop is aangesloten.",
    statuses: { pending: "In afwachting", active: "Actief", revoked: "Ingetrokken door medewerker", declined: "Geweigerd", closed: "Gesloten" },
  },
  consent: {
    title: "Toestemmingscentrum",
    subtitle: "Wie ziet wat, waarom, sinds wanneer — en hoe je het op elk moment intrekt.",
    orgSharing: "Delen met je organisatie",
    orgSharingBody: "Alleen geaggregeerde, anonieme signalen. Nooit je berichten, notities of toelichtingen.",
    care: "Zorgprofessionals (GGZ)",
    oh: "Bedrijfsarts (arbo)",
    noRequests: "Geen verzoeken of actieve toestemmingen.",
    requestFrom: "Verzoek van",
    grantedOn: "Gegeven op",
    revokedOn: "Ingetrokken op",
    categories: "Wat wordt gedeeld",
    accept: "Geselecteerde delen",
    decline: "Weigeren",
    revoke: "Toestemming intrekken",
    revokeConfirm: "Toestemming intrekken? Toegang vervalt direct.",
    review: "Verzoek bekijken",
    activeLabel: "Actief",
    pendingLabel: "Wacht op jouw besluit",
    whyTitle: "Waarom dit belangrijk is",
    whyBody: "Je werkgever betaalt voor een licentie, niet voor toegang tot jouw gegevens. Alleen jij bepaalt wat een professional mag zien — per relatie, per categorie.",
    cat: {
      wellbeing: "Welzijnstrend", mood: "Stemming", stress_anxiety: "Stress & angst", sleep: "Slaap", checkins: "Check-ins (alleen scores)",
      weekly_summaries: "Weekoverzichten", work_capacity: "Signalen werkvermogen", reintegration_notes: "Re-integratienotities",
      crisis_alerts: "Crisismeldingen", safety_plan: "Veiligheidsplan", goals: "Doelen", journal: "Dagboek", ai_conversations: "AI-gesprekken",
    },
    mode: "Deelmodus",
  },
  flm: {
    title: "Mijn team",
    subtitle: "Alleen deelname en afgesproken geaggregeerde trends — nooit individuele inhoud.",
    privacyNote: "Cijfers worden verborgen voor groepen kleiner dan de privacydrempel.",
  },
  common: {
    seatsUnit: "seats",
    seatUnit: "seat",
    none: "—",
    back: "Terug",
    open: "Openen",
    refresh: "Vernieuwen",
    privacyBadge: "Privacyveilig geaggregeerd",
  },
};
